import React, { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Row, Col, Card, Form, Button, Spinner } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";

const emptyForm = {
 warehouseName: "",
 warehouseCode: "",
 addressLine1: "",
 addressLine2: "",
 city: "",
 state: "",
 pincode: "",
 country: "India",
 contactPerson: "",
 phone: "",
 email: "",
 status: "Active",
 isDefault: false,
};

const WarehouseForm = () => {
 const { id } = useParams();
 const navigate = useNavigate();
 const isEdit = Boolean(id);
 const [loading, setLoading] = useState(isEdit);
 const [saving, setSaving] = useState(false);
 const [form, setForm] = useState(emptyForm);

 useEffect(() => {
 if (!isEdit) return;

 const load = async () => {
 try {
 setLoading(true);
 const res = await api.get(`/warehouses/${id}`);
 if (res.data.success) {
 setForm({
 ...emptyForm,
 ...res.data.data,
 });
 }
 } catch (error) {
 swal("Error", "Failed to load warehouse", "error");
 navigate("/warehouses");
 } finally {
 setLoading(false);
 }
 };

 load();
 }, [id, isEdit, navigate]);

 const handleChange = (e) => {
 const { name, value, type, checked } = e.target;
 setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
 };

 const handleSubmit = async (e) => {
 e.preventDefault();

 if (!form.warehouseName.trim()) return swal("Warning", "Enter warehouse name", "warning");
 if (!form.warehouseCode.trim()) return swal("Warning", "Enter warehouse code", "warning");

 try {
 setSaving(true);
 const payload = {
 ...form,
 warehouseName: form.warehouseName.trim(),
 warehouseCode: form.warehouseCode.trim().toUpperCase(),
 };

 const res = isEdit
 ? await api.patch(`/warehouses/${id}`, payload)
 : await api.post("/warehouses", payload);

 if (res.data.success) {
 await swal("Success", `Warehouse ${isEdit ? "updated" : "created"} successfully`, "success");
 navigate("/warehouses");
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to save warehouse", "error");
 } finally {
 setSaving(false);
 }
 };

 if (loading) {
 return (
 <div className="text-center py-5">
 <Spinner animation="border" />
 </div>
 );
 }

 return (
 <Fragment>
 <PageTitle activeMenu={isEdit ? "Edit Warehouse" : "Add Warehouse"} motherMenu="Inventory" />
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">{isEdit ? "Edit Warehouse" : "Add Warehouse"}</Card.Title>
 <Button variant="secondary" onClick={() => navigate("/warehouses")}>
 Back
 </Button>
 </Card.Header>
 <Card.Body>
 <Form onSubmit={handleSubmit}>
 <Row className="g-3">
 <Col md={6}>
 <Form.Label>Warehouse Name *</Form.Label>
 <Form.Control name="warehouseName" value={form.warehouseName} onChange={handleChange} />
 </Col>
 <Col md={6}>
 <Form.Label>Warehouse Code *</Form.Label>
 <Form.Control name="warehouseCode" value={form.warehouseCode} onChange={handleChange} />
 </Col>
 <Col md={6}>
 <Form.Label>Address Line 1</Form.Label>
 <Form.Control name="addressLine1" value={form.addressLine1} onChange={handleChange} />
 </Col>
 <Col md={6}>
 <Form.Label>Address Line 2</Form.Label>
 <Form.Control name="addressLine2" value={form.addressLine2} onChange={handleChange} />
 </Col>
 <Col md={4}>
 <Form.Label>City</Form.Label>
 <Form.Control name="city" value={form.city} onChange={handleChange} />
 </Col>
 <Col md={4}>
 <Form.Label>State</Form.Label>
 <Form.Control name="state" value={form.state} onChange={handleChange} />
 </Col>
 <Col md={4}>
 <Form.Label>Pincode</Form.Label>
 <Form.Control name="pincode" value={form.pincode} onChange={handleChange} />
 </Col>
 <Col md={4}>
 <Form.Label>Country</Form.Label>
 <Form.Control name="country" value={form.country} onChange={handleChange} />
 </Col>
 <Col md={4}>
 <Form.Label>Contact Person</Form.Label>
 <Form.Control name="contactPerson" value={form.contactPerson} onChange={handleChange} />
 </Col>
 <Col md={4}>
 <Form.Label>Phone</Form.Label>
 <Form.Control name="phone" value={form.phone} onChange={handleChange} />
 </Col>
 <Col md={6}>
 <Form.Label>Email</Form.Label>
 <Form.Control name="email" value={form.email} onChange={handleChange} />
 </Col>
 <Col md={3}>
 <Form.Label>Status</Form.Label>
 <Form.Select name="status" value={form.status} onChange={handleChange}>
 <option value="Active">Active</option>
 <option value="Inactive">Inactive</option>
 </Form.Select>
 </Col>
 <Col md={3} className="d-flex align-items-end">
 <Form.Check
 type="checkbox"
 label="Default warehouse"
 name="isDefault"
 checked={form.isDefault}
 onChange={handleChange}
 />
 </Col>
 </Row>

 <div className="d-flex justify-content-end gap-2 mt-4">
 <Button variant="light" type="button" onClick={() => navigate("/warehouses")}>
 Cancel
 </Button>
 <Button type="submit" disabled={saving}>
 {saving ? "Saving..." : "Save Warehouse"}
 </Button>
 </div>
 </Form>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default WarehouseForm;
